import { useDispatch, useSelector } from 'react-redux'
import React, {
    useEffect,
    useCallback,
    useState,
    useLayoutEffect,
    useRef,
} from 'react'

// antd
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    Row,
    Col,
    Avatar,
    Typography,
    Badge,
    Card,
    Tooltip,
    Popover,
    Button,
    Modal,
} from 'antd'

// slices
import {
    fetchClients,
    clientsState,
    updateClient,
    fetchProjectsForClientModal,
    resetClientModalProjects,
    setClientModalProjectSearch,
} from '../clients.slice'

// css
import styles from './ClientsList.module.css'

// components
import ProjectMultiSelect from '../../../components/ProjectMultiSelect'
import { showActionNotification } from '../../../components/notification'
import { getRandomAvatar } from '../../../assets/clients/avatar'
import ClientsEmptyState from './ClientsEmptyState'

const { Text } = Typography

// Helper function to get initials from name
const getInitials = (client) => {
    // Check if firstName and lastName exist separately
    if (client?.firstName && client?.lastName) {
        return (
            client?.firstName.charAt(0).toUpperCase() +
            client?.lastName.charAt(0).toUpperCase()
        )
    }

    // Check if there's a full name string (userName or name)
    const fullName = client?.userName || client?.name || ''
    if (!fullName) return ''

    // Split the name by spaces
    const nameParts = fullName.trim().split(/\s+/)

    if (nameParts.length >= 2) {
        // Has first and last name
        return (
            nameParts[0].charAt(0).toUpperCase() +
            nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        )
    } else if (nameParts.length === 1) {
        // Only first name
        return nameParts[0].charAt(0).toUpperCase()
    }

    return ''
}

const ClientsList = ({
    onEditClient,
    apiSearch = '',
    includeDeleted,
    projectFilterIds = [],
    viewMode = 'card',
    canEdit = true,
    canDelete = true,
}) => {
    const dispatch = useDispatch()
    const { clients, status, isNext, clientPage } = useSelector(clientsState)
    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)

    const [openPopovers, setOpenPopovers] = useState({})
    const [selectedProjectsToAdd, setSelectedProjectsToAdd] = useState({})
    const [updatingClients, setUpdatingClients] = useState({})
    const autoFillPagesRef = useRef(0)

    useEffect(() => {
        autoFillPagesRef.current = 0
    }, [apiSearch, includeDeleted, projectFilterIds])

    const clientFilter = useCallback(
        () =>
            projectFilterIds.length > 0 ? { projectIds: projectFilterIds } : {},
        [projectFilterIds]
    )

    const handleArchiveClient = useCallback(
        (client) => {
            if (!canDelete) return
            const clientId = client?._id
            if (!clientId) return
            const isArchived = Boolean(client?.isDeleted)

            Modal.confirm({
                title: isArchived ? 'Unarchive client?' : 'Archive client?',
                content: isArchived
                    ? 'This will restore the client back to the active list.'
                    : 'This will archive the client and hide them from the list.',
                okText: isArchived ? 'Unarchive' : 'Archive',
                okButtonProps: isArchived ? {} : { danger: true },
                cancelText: 'Cancel',
                onOk: async () => {
                    setUpdatingClients((prev) => ({
                        ...prev,
                        [clientId]: true,
                    }))
                    try {
                        const result = await dispatch(
                            updateClient({
                                id: clientId,
                                data: {
                                    isDeleted: !isArchived,
                                },
                            })
                        )

                        if (result?.payload?.data?.success) {
                            showActionNotification({
                                type: 'success',
                                message: isArchived
                                    ? 'Client unarchived'
                                    : 'Client archived',
                                subMessage: client?.userName || client?.email,
                                notificationKey: isArchived
                                    ? 'client-unarchived'
                                    : 'client-archived',
                            })
                            await dispatch(
                                fetchClients({
                                    skip: 0,
                                    limit: 10,
                                    search: apiSearch,
                                    filter: clientFilter(),
                                    includeDeleted,
                                })
                            )
                        } else {
                            showActionNotification({
                                type: 'error',
                                message: isArchived
                                    ? 'Could not unarchive client'
                                    : 'Could not archive client',
                                subMessage:
                                    result?.payload?.data?.message ||
                                    'Please try again.',
                                notificationKey: isArchived
                                    ? 'client-unarchive-failed'
                                    : 'client-archive-failed',
                            })
                        }
                    } catch {
                        showActionNotification({
                            type: 'error',
                            message: isArchived
                                ? 'Could not unarchive client'
                                : 'Could not archive client',
                            subMessage: 'Please try again.',
                            notificationKey: isArchived
                                ? 'client-unarchive-failed'
                                : 'client-archive-failed',
                        })
                    } finally {
                        setUpdatingClients((prev) => ({
                            ...prev,
                            [clientId]: false,
                        }))
                    }
                },
            })
        },
        [dispatch, apiSearch, includeDeleted, clientFilter, canDelete]
    )

    // Initial / filter fetch is dispatched from `clients/index.js` — avoid duplicate calls here.

    const fetchMoreClients = useCallback(() => {
        if (isNext && status !== 'loading') {
            const nextPage = clientPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                fetchClients({
                    skip: skip,
                    limit: 10,
                    search: apiSearch,
                    filter: clientFilter(),
                    includeDeleted,
                })
            )
        }
    }, [
        dispatch,
        isNext,
        status,
        clientPage,
        apiSearch,
        includeDeleted,
        clientFilter,
    ])

    useLayoutEffect(() => {
        if (!isNext || status !== 'loaded') return
        if (autoFillPagesRef.current >= 30) return

        const el = document.getElementById('clients-scroll-container')
        if (!el) return

        const tolerance = 4
        const noOverflow = el.scrollHeight <= el.clientHeight + tolerance

        if (noOverflow) {
            autoFillPagesRef.current += 1
            fetchMoreClients()
        }
    }, [clients.length, isNext, status, fetchMoreClients])

    // Handle popover open/close
    const handlePopoverOpenChange = (clientId, open) => {
        if (open && !canEdit) return
        setOpenPopovers((prev) => ({ ...prev, [clientId]: open }))
        if (open) {
            // Fetch projects when popover opens
            dispatch(resetClientModalProjects())
            // setExpandedClientProjects((prev) => ({
            //     ...prev,
            //     [clientId]: false,
            // }))
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )
        } else {
            // Reset selected projects when closing
            setSelectedProjectsToAdd((prev) => {
                const newState = { ...prev }
                delete newState[clientId]
                return newState
            })
        }
    }

    // Debounced search for projects
    const handleProjectSearch = debounce((value, clientId) => {
        dispatch(setClientModalProjectSearch(value))
        dispatch(
            fetchProjectsForClientModal({
                skip: 0,
                filter: value ? { search: value } : {},
                limit: 10,
                search: value,
            })
        )
    }, 300)

    // Load more projects on scroll
    const handleProjectScroll = (e) => {
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            clientModalIsNext &&
            clientModalListStatus !== 'loading'
        ) {
            const nextPage = clientModalProjectPage + 1
            const skip = (nextPage - 1) * 10

            dispatch(
                fetchProjectsForClientModal({
                    skip: skip,
                    filter: clientModalProjectSearch
                        ? { search: clientModalProjectSearch }
                        : {},
                    limit: 10,
                    search: clientModalProjectSearch,
                })
            )
        }
    }

    // Remove project from client
    const handleRemoveProject = async (clientId, projectId, clientName) => {
        setUpdatingClients((prev) => ({ ...prev, [clientId]: true }))

        try {
            const client = clients.find((c) => c?._id === clientId)
            if (!client) return

            const updatedProjectIds = client?.projects
                .filter((p) => p?._id === projectId)
                .map((p) => p?._id)

            const result = await dispatch(
                updateClient({
                    id: clientId,
                    data: {
                        removeProjectIds: updatedProjectIds,
                    },
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    message: 'Project removed successfully',
                    subMessage: clientName,
                    actionButtonText: 'Undo',
                    notificationKey: 'client-created',
                })

                await dispatch(
                    fetchClients({
                        skip: 0,
                        limit: 10,
                        search: apiSearch,
                        filter: clientFilter(),
                        includeDeleted,
                    })
                )
            } else {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to remove project',
                    subMessage:
                        result?.payload?.data?.message || 'Please try again.',
                    notificationKey: 'client-project-remove-failed',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to remove project',
                subMessage: 'Please try again.',
                notificationKey: 'client-project-remove-failed',
            })
        } finally {
            setUpdatingClients((prev) => ({ ...prev, [clientId]: false }))
        }
    }

    // Add projects to client
    const handleAddProjects = async (clientId) => {
        const projectsToAdd = selectedProjectsToAdd[clientId] || []
        if (projectsToAdd.length === 0) {
            showActionNotification({
                type: 'warning',
                message: 'Please select at least one project',
                notificationKey: 'client-projects-add-failed',
            })
            return
        }

        setUpdatingClients((prev) => ({ ...prev, [clientId]: true }))
        try {
            const client = clients.find((c) => c._id === clientId)
            if (!client) return

            const existingProjectIds = client?.projects?.map((p) => p?._id)
            const newProjectIds = [...existingProjectIds, ...projectsToAdd]

            const result = await dispatch(
                updateClient({
                    id: clientId,
                    data: {
                        addProjectIds: newProjectIds,
                    },
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    type: 'success',
                    message: 'Projects added successfully',
                    subMessage: client?.userName,
                    notificationKey: 'client-projects-added',
                })
                setSelectedProjectsToAdd((prev) => {
                    const newState = { ...prev }
                    delete newState[clientId]
                    return newState
                })
                // Refresh clients list
                await dispatch(
                    fetchClients({
                        skip: 0,
                        limit: 10,
                        search: apiSearch,
                        filter: clientFilter(),
                        includeDeleted,
                    })
                )
            } else {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to add projects',
                    subMessage:
                        result?.payload?.data?.message || 'Please try again.',
                    notificationKey: 'client-projects-add-failed',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to add projects',
                subMessage: 'Please try again.',
                notificationKey: 'client-projects-add-failed',
            })
        } finally {
            setUpdatingClients((prev) => ({ ...prev, [clientId]: false }))
        }
    }

    // Format project options (exclude already assigned projects)
    const getAvailableProjects = (client) => {
        const assignedProjectIds = new Set(
            (client?.projects || []).map((p) => p._id)
        )
        return clientModalProjectsListData.filter(
            (project) => !assignedProjectIds.has(project._id)
        )
    }

    // Render project management popover content
    const renderProjectManagementContent = (client) => {
        const isLoading = updatingClients[client?._id]
        const availableProjects = getAvailableProjects(client)
        const projectCount = client?.projects?.length || 0

        return (
            <div
                style={{
                    width: '340px',
                    // position:fixed on the overlay (via .clients-projects-popover CSS class)
                    // escapes overflow:hidden on body/layout containers.
                    // maxHeight here is the CONTENT area only — the title is rendered
                    // separately by Ant Design above this div (adds ~50px).
                    // 60vh covers most viewport sizes; 330px caps it on large screens.
                    maxHeight: 'min(calc(60vh - 50px), 330px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: '0 16px 16px',
                }}
            >
                {/* Scrollable projects section — grows to fill available space */}
                <div
                    style={{
                        flex: '1 1 auto',
                        // min-height:0 allows the flex child to shrink below
                        // its natural content height so overflow:auto works.
                        minHeight: 0,
                        overflowY: 'auto',
                        // Extra padding so chip hover shadow isn't clipped
                        padding: '2px 4px 12px 2px',
                    }}
                    className={styles.popoverScrollArea}
                >
                    <div className={styles.popoverProjectsHeader}>
                        <div className={styles.popoverProjectsTitle}>
                            Current Projects
                            <span className={styles.popoverProjectsCount}>
                                {projectCount}
                            </span>
                        </div>
                    </div>

                    {projectCount > 0 ? (
                        <div className={styles.popoverProjectsList}>
                            {(client?.projects || []).map((project) => (
                                <div
                                    key={project._id}
                                    className={styles.popoverProjectChip}
                                    title={project?.name || ''}
                                >
                                    <Icon
                                        icon="mdi:folder-outline"
                                        className={
                                            styles.popoverProjectChipIcon
                                        }
                                        color={project?.colorCode || '#6366f1'}
                                    />
                                    <span
                                        className={
                                            styles.popoverProjectChipText
                                        }
                                    >
                                        {project?.name || 'N/A'}
                                    </span>
                                    <button
                                        type="button"
                                        className={
                                            styles.popoverProjectRemoveBtn
                                        }
                                        onClick={() =>
                                            handleRemoveProject(
                                                client?._id,
                                                project?._id,
                                                client?.userName
                                            )
                                        }
                                        disabled={isLoading}
                                        aria-label="Remove project"
                                        title="Remove project"
                                    >
                                        <Icon icon="mdi:close" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.popoverEmptyState}>
                            No projects assigned
                        </div>
                    )}
                </div>

                {/* Add Projects — sticky footer, never pushed off screen */}
                <div
                    style={{
                        flexShrink: 0,
                        borderTop: '1px solid #e2e8f0',
                        paddingTop: '10px',
                    }}
                >
                    <div
                        style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#64748b',
                            marginBottom: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Add Projects
                    </div>
                    <div className="project-popover-select">
                        <ProjectMultiSelect
                            variant="form"
                            placeholder="Search and select projects..."
                            projects={availableProjects}
                            showSearch
                            filterOption={false}
                            onSearch={(value) =>
                                handleProjectSearch(value, client?._id)
                            }
                            onPopupScroll={handleProjectScroll}
                            loading={clientModalListStatus === 'loading'}
                            page={clientModalProjectPage}
                            value={selectedProjectsToAdd[client?._id] || []}
                            onChange={(values) => {
                                setSelectedProjectsToAdd((prev) => ({
                                    ...prev,
                                    [client?._id]: values,
                                }))
                            }}
                            style={{
                                width: '100%',
                                marginBottom: '12px',
                            }}
                        />
                    </div>
                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={() => handleAddProjects(client?._id)}
                        disabled={
                            isLoading ||
                            !selectedProjectsToAdd[client?._id] ||
                            selectedProjectsToAdd[client?._id]?.length === 0
                        }
                        loading={isLoading}
                        className={styles.addProjectsButton}
                    >
                        <Icon
                            icon="mdi:plus-circle"
                            className={styles.addProjectsButtonIcon}
                        />
                        Add Projects
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.clientsListContainer}>
            {status === 'loading' && clients.length === 0 ? (
                <div className={styles.loadingContainer}>
                    <Icon
                        icon="line-md:loading-loop"
                        style={{ fontSize: 48, color: '#6366f1' }}
                    />
                </div>
            ) : clients.length > 0 ? (
                <InfiniteScroll
                    dataLength={clients.length}
                    next={fetchMoreClients}
                    hasMore={isNext}
                    initialLoad={false}
                    scrollThreshold="200px"
                    loader={
                        status === 'loading' ? (
                            <div className={styles.loadingContainer}>
                                <Icon
                                    icon="line-md:loading-loop"
                                    style={{ fontSize: 26, color: '#6366f1' }}
                                />
                            </div>
                        ) : null
                    }
                    scrollableTarget="clients-scroll-container"
                >
                    <Row
                        gutter={[20, 20]}
                        className={styles.clientsGrid}
                        style={{
                            paddingTop: '5px',
                        }}
                    >
                        {clients.map((client) => (
                            <Col
                                key={client?._id}
                                xs={24}
                                sm={viewMode === 'list' ? 24 : 12}
                                md={viewMode === 'list' ? 24 : 12}
                                lg={viewMode === 'list' ? 24 : 8}
                                xl={viewMode === 'list' ? 24 : 6}
                            >
                                <Badge.Ribbon
                                    text="Archived"
                                    color="#adadad"
                                    className={styles.clientArchivedRibbon}
                                    style={{
                                        display: client?.isDeleted
                                            ? 'inline-block'
                                            : 'none',
                                    }}
                                >
                                    <Card
                                        className={`${styles.clientCard} ${
                                            viewMode === 'list'
                                                ? styles.clientCardList
                                                : ''
                                        }`}
                                        hoverable={false}
                                    >
                                        <div
                                            className={`${styles.cardContent} ${
                                                viewMode === 'list'
                                                    ? styles.cardContentList
                                                    : ''
                                            }`}
                                        >
                                            <div
                                                className={`${styles.cardHeader} ${
                                                    viewMode === 'list'
                                                        ? styles.cardHeaderList
                                                        : ''
                                                }`}
                                            >
                                                {(() => {
                                                    const avatarSrc =
                                                        client?.profilePicture ||
                                                        getRandomAvatar({
                                                            gender: client?.gender,
                                                            seed:
                                                                client?._id ||
                                                                client?.email ||
                                                                client?.userName,
                                                        })
                                                    return (
                                                        <Avatar
                                                            size={44}
                                                            className={
                                                                styles.clientAvatar
                                                            }
                                                            src={avatarSrc}
                                                            icon={
                                                                !avatarSrc &&
                                                                !getInitials(
                                                                    client
                                                                ) ? (
                                                                    <Icon
                                                                        icon="mdi:account"
                                                                        style={{
                                                                            fontSize: 22,
                                                                        }}
                                                                    />
                                                                ) : null
                                                            }
                                                            style={{
                                                                fontSize: 14,
                                                            }}
                                                        >
                                                            {!avatarSrc
                                                                ? getInitials(
                                                                      client
                                                                  )
                                                                : null}
                                                        </Avatar>
                                                    )
                                                })()}
                                                <div
                                                    className={
                                                        styles.clientInfo
                                                    }
                                                >
                                                    <Text
                                                        className={
                                                            styles.clientName
                                                        }
                                                    >
                                                        {client?.userName ||
                                                            'N/A'}
                                                    </Text>
                                                    <Text
                                                        className={
                                                            styles.clientEmail
                                                        }
                                                    >
                                                        {client?.email || 'N/A'}
                                                    </Text>
                                                </div>
                                                <div
                                                    className={`${styles.clientActions} ${
                                                        viewMode === 'list'
                                                            ? styles.clientActionsList
                                                            : ''
                                                    }`}
                                                >
                                                    {canEdit ? (
                                                        <Tooltip title="Edit Client">
                                                            <button
                                                                type="button"
                                                                className={
                                                                    styles.actionIconBtn
                                                                }
                                                                disabled={
                                                                    updatingClients[
                                                                        client?._id
                                                                    ]
                                                                }
                                                                onClick={() =>
                                                                    onEditClient?.(
                                                                        client
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    width={18}
                                                                    height={18}
                                                                    icon="mynaui:edit"
                                                                />
                                                            </button>
                                                        </Tooltip>
                                                    ) : null}
                                                    {canDelete ? (
                                                        <Tooltip
                                                            title={
                                                                client?.isDeleted
                                                                    ? 'Unarchive Client'
                                                                    : 'Archive Client'
                                                            }
                                                        >
                                                            <button
                                                                type="button"
                                                                className={
                                                                    styles.actionIconBtn
                                                                }
                                                                disabled={
                                                                    updatingClients[
                                                                        client?._id
                                                                    ]
                                                                }
                                                                onClick={() =>
                                                                    handleArchiveClient(
                                                                        client
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    width={18}
                                                                    height={18}
                                                                    icon={
                                                                        client?.isDeleted
                                                                            ? 'mdi:archive-arrow-up-outline'
                                                                            : 'mdi:archive-outline'
                                                                    }
                                                                />
                                                            </button>
                                                        </Tooltip>
                                                    ) : null}
                                                </div>
                                            </div>

                                            <Popover
                                                content={
                                                    canEdit
                                                        ? renderProjectManagementContent(
                                                              client
                                                          )
                                                        : null
                                                }
                                                arrow={false}
                                                title={
                                                    canEdit ? (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: '8px',
                                                                fontSize:
                                                                    '16px',
                                                                fontWeight: 600,
                                                                color: '#1e293b',
                                                            }}
                                                        >
                                                            <Icon
                                                                icon="mdi:folder-multiple-outline"
                                                                style={{
                                                                    fontSize:
                                                                        '16px',
                                                                    color: '#4f46e5',
                                                                }}
                                                            />
                                                            Manage Projects
                                                        </div>
                                                    ) : null
                                                }
                                                trigger={canEdit ? 'click' : []}
                                                open={
                                                    canEdit
                                                        ? openPopovers[
                                                              client?._id
                                                          ]
                                                        : false
                                                }
                                                onOpenChange={(open) =>
                                                    handlePopoverOpenChange(
                                                        client?._id,
                                                        open
                                                    )
                                                }
                                                placement="bottomLeft"
                                                classNames={{
                                                    root: 'clients-projects-popover',
                                                }}
                                                styles={{
                                                    root: {
                                                        borderRadius: '12px',
                                                    },
                                                    body: {
                                                        padding: 0,
                                                        overflow: 'hidden',
                                                        borderRadius: '12px',
                                                    },
                                                }}
                                            >
                                                <div
                                                    className={`${styles.projectsSection} ${
                                                        viewMode === 'list'
                                                            ? styles.projectsSectionList
                                                            : ''
                                                    }`}
                                                    style={{
                                                        cursor: canEdit
                                                            ? 'pointer'
                                                            : 'default',
                                                    }}
                                                >
                                                    {(() => {
                                                        const projects =
                                                            client?.projects ||
                                                            []
                                                        const count =
                                                            projects.length
                                                        const visible =
                                                            projects.slice(0, 2)
                                                        const remaining =
                                                            Math.max(
                                                                0,
                                                                count - 2
                                                            )

                                                        return (
                                                            <>
                                                                <div
                                                                    className={
                                                                        styles.projectsHeader
                                                                    }
                                                                >
                                                                    <div
                                                                        className={
                                                                            styles.projectsHeaderLeft
                                                                        }
                                                                    >
                                                                        <Icon
                                                                            icon="mdi:folders-outline"
                                                                            className={
                                                                                styles.projectsIcon
                                                                            }
                                                                        />
                                                                        <Text
                                                                            className={
                                                                                styles.projectsLabel
                                                                            }
                                                                        >
                                                                            Projects
                                                                        </Text>
                                                                        <div
                                                                            className={
                                                                                styles.projectsBadge
                                                                            }
                                                                        >
                                                                            {
                                                                                count
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {count > 0 ? (
                                                                    <div
                                                                        className={
                                                                            styles.projectPillsRow
                                                                        }
                                                                    >
                                                                        {visible.map(
                                                                            (
                                                                                project,
                                                                                idx
                                                                            ) => {
                                                                                return (
                                                                                    <div
                                                                                        key={
                                                                                            project?._id ||
                                                                                            `${idx}-${project?.name}`
                                                                                        }
                                                                                        className={
                                                                                            styles.projectPill
                                                                                        }
                                                                                    >
                                                                                        <Icon
                                                                                            icon={
                                                                                                'mdi:folder-outline'
                                                                                            }
                                                                                            width={
                                                                                                14
                                                                                            }
                                                                                            height={
                                                                                                14
                                                                                            }
                                                                                            style={{
                                                                                                color:
                                                                                                    project?.colorCode ||
                                                                                                    '#6366f1',
                                                                                                minWidth:
                                                                                                    '14px',
                                                                                            }}
                                                                                        />

                                                                                        <span
                                                                                            className={
                                                                                                styles.projectPillText
                                                                                            }
                                                                                        >
                                                                                            {project?.name ||
                                                                                                'N/A'}
                                                                                        </span>
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        )}

                                                                        {remaining >
                                                                            0 && (
                                                                            <div
                                                                                className={
                                                                                    styles.moreProjectsPill
                                                                                }
                                                                                title={`${remaining} more project(s)`}
                                                                            >
                                                                                <Icon
                                                                                    icon="mdi:plus-circle-outline"
                                                                                    className={
                                                                                        styles.moreIcon
                                                                                    }
                                                                                />
                                                                                {
                                                                                    remaining
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={
                                                                            styles.noProjectsPreview
                                                                        }
                                                                    >
                                                                        Click to
                                                                        add
                                                                        projects
                                                                    </div>
                                                                )}
                                                            </>
                                                        )
                                                    })()}
                                                </div>
                                            </Popover>
                                        </div>
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                        ))}
                    </Row>
                </InfiniteScroll>
            ) : (
                <ClientsEmptyState search={apiSearch} />
            )}
        </div>
    )
}

export default ClientsList
